// Finished custom widgets, written with the kit the way we want agents to
// write theirs. The gallery shows them live (with example data, labelled as
// such), and get_widget_guide hands their code to an agent as a model.

export interface WidgetExample {
  id: string;
  name: string;
  blurb: string;
  /** what it reads: a stream shape, or nothing when it works it out itself */
  reads: "items" | "points" | "json" | null;
  /** what its stream holds, for the agent reading the example */
  data?: string;
  /** what to say to the agent to make one like it with real data */
  ask: string;
  size: [number, number];
  html: string;
}

const COMPARE = `<div class="cb-fill" id="main" hidden>
  <div class="cb-split" style="align-items:flex-start">
    <div class="cb-stack" style="--gap:0.15rem;min-width:0">
      <span class="cb-value is-md" id="now"></span>
      <span class="cb-label cb-clip" id="what"></span>
    </div>
    <span class="cb-delta" id="delta"></span>
  </div>
  <span class="cb-sub cb-clip" id="pace" hidden></span>
  <div class="cb-chart" id="chart"></div>
</div>
<div class="cb-empty" id="empty"><b>No history yet</b>Send this stream a point a day to compare months.</div>
<style>
  [data-size="s"] #chart { display: none; }
</style>
<script>
  var M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  function span(m, n) { return M[m] + " 1" + (n > 1 ? "–" + n : ""); }
  function running(byDay, y, m, days) {
    var out = [], sum = 0;
    for (var i = 1; i <= days; i++) out.push(sum += byDay[new Date(y, m, i).getTime()] || 0);
    return out;
  }
  board.onData(function (d) {
    // counts add up (signups, orders); for a level like MRR, compare its last value instead
    var byDay = {}, first = Infinity;
    board.bucket(d.points, "day", "sum").forEach(function (p) { byDay[p.t] = p.v; first = Math.min(first, p.t); });
    var has = first < Infinity;
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    var now = new Date(), y = now.getFullYear(), m = now.getMonth(), py = m ? y : y - 1, pm = m ? m - 1 : 11;
    var cur = running(byDay, y, m, now.getDate()), mtd = cur[cur.length - 1];
    // last month counts only if the history reaches back to its first day
    var whole = first <= new Date(py, pm, 1).getTime();
    var prev = whole ? running(byDay, py, pm, new Date(y, m, 0).getDate()) : [];
    var same = Math.min(cur.length, prev.length), then = prev[same - 1], from = new Date(first);
    board.count("#now", mtd);
    document.getElementById("what").textContent = (d.label || "So far") + ", " + span(m, cur.length) +
      (whole ? " vs " + span(pm, same) : " · history starts " + M[from.getMonth()] + " " + from.getDate());
    var delta = document.getElementById("delta");
    delta.textContent = whole ? board.delta(mtd, then) : "";
    delta.className = "cb-delta " + (mtd >= then ? "is-good" : "is-bad");
    delta.hidden = !delta.textContent;
    // what it means: where the month is heading, against how the last one ended, once a few whole days are in
    var p = board.pace(d.points, { period: "month" }), pace = document.getElementById("pace");
    pace.textContent = !p ? "" : p.projected === null ? "The pace shows after a few days" : "On pace for " + board.fmt(Math.round(p.projected)) + (p.prevTotal !== null ? ", after " + board.fmt(p.prevTotal) + " in " + p.prevName : "");
    pace.hidden = !pace.textContent;
    board.line("#chart", [{ label: M[m], points: cur, color: "var(--accent)" }, { label: M[pm], points: prev, color: "var(--fg-4)" }], {
      labels: (prev.length ? prev : cur).map(function (_, i) { return "Day " + (i + 1); }),
      zero: true,
      highlight: "last",
    });
  });
</script>`;

const GOAL = `<div class="cb-fill" id="main" hidden>
  <div class="cb-split" style="align-items:flex-start">
    <div class="cb-stack" style="--gap:0.15rem;min-width:0">
      <span class="cb-value is-md" id="now"></span>
      <span class="cb-label cb-clip" id="of"></span>
    </div>
    <span class="cb-tag is-accent" id="pct"></span>
  </div>
  <div class="cb-bar" id="bar"></div>
  <span class="cb-sub cb-clip" id="eta"></span>
  <div class="cb-chart" id="chart"></div>
</div>
<div class="cb-empty" id="empty"><b>Nothing to measure yet</b>Send this stream its number, with a point a day, to see when it gets there.</div>
<style>
  #bar { margin: 0.46rem 0 0.15rem; }
  [data-size="s"] #chart { display: none; }
</style>
<script>
  // the user's target lives in the code
  var GOAL = 10000;
  board.onData(function (d) {
    var has = typeof d.value === "number";
    document.getElementById("main").hidden = !has;
    document.getElementById("empty").hidden = has;
    if (!has) return;
    board.count("#now", d.value);
    document.getElementById("of").textContent = "of " + board.fmt(GOAL) + (d.label ? " " + d.label : "");
    var pct = Math.max(0, Math.min(100, (d.value / GOAL) * 100));
    // short of the goal never reads as 100%: below it, round down
    var shown = d.value < GOAL ? Math.min(99, Math.floor(pct)) : 100;
    document.getElementById("pct").textContent = board.fmt(shown, { style: "percent", decimals: 0 });
    document.getElementById("bar").style.setProperty("--pct", pct.toFixed(1) + "%");
    // what it means: when it gets there at the rate of the last four weeks
    var t = board.trend(d.points, { days: 28 }), when = t && t.reach(GOAL), eta = document.getElementById("eta");
    var date = function (ms) {
      var x = new Date(ms);
      return x.toLocaleDateString("en-US", { month: "short", day: "numeric", year: x.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
    };
    // each line short enough to read whole on the smallest tile
    eta.textContent = d.value >= GOAL ? "Goal reached" : when ? "At this pace, " + board.fmt(GOAL) + " by " + date(when) : !t ? "Needs a few recent days to say when" : t.direction === "up" ? "Rising, but more than ten years away" : t.direction === "down" ? "Falling lately, so not on course yet" : "Flat lately, so not on course yet";
    board.line("#chart", d.points, { goal: GOAL, highlight: "last" });
  });
</script>`;

const FUNNEL = `<div class="cb-fill">
  <div class="head">
    <span class="cb-value is-md" id="rate">—</span>
    <span class="cb-label" id="rate-label"></span>
  </div>
  <span class="cb-sub cb-clip" id="leak" hidden></span>
  <div class="steps" id="steps"></div>
</div>
<style>
  .head { display: flex; align-items: baseline; gap: 0.62rem; min-width: 0; }
  .head .cb-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .steps { flex: 1; min-height: 0; display: flex; flex-direction: column; justify-content: space-around; }
  .step { display: grid; grid-template-columns: minmax(4.62rem, 28%) 1fr 4.31rem; align-items: center; gap: 0.77rem; }
  .name { font-size: 0.96rem; color: var(--fg-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .track { height: 1.23rem; display: flex; justify-content: center; }
  .track i { height: 100%; min-width: 0.46rem; border-radius: 0.38rem; background: var(--accent); transition: width .7s cubic-bezier(.2,.9,.3,1); }
  .step .cb-num { font-size: 1rem; text-align: right; }
  .conv { font-size: 0.85rem; color: var(--fg-3); text-align: center; padding-left: calc(28% + 0.77rem); padding-right: 5.08rem; font-variant-numeric: tabular-nums; }
  .conv.is-worst { color: var(--warn); font-weight: 600; }
  [data-size="s"] .head, [data-size="s"] .conv, [data-size="s"] #leak { display: none; }
</style>
<script>
  board.onData(function (d) {
    var steps = (d.items || []).filter(function (s) { return typeof s.value === "number"; });
    var el = document.getElementById("steps");
    if (steps.length < 2) {
      el.innerHTML = '<div class="cb-empty"><b>No steps yet</b>Send a list of stages with a count for each.</div>';
      return;
    }
    var first = steps[0], last = steps[steps.length - 1];
    board.count("#rate", first.value ? (last.value / first.value) * 100 : null, { format: { style: "percent", decimals: 1 } });
    document.getElementById("rate-label").textContent = "from " + first.label + " to " + last.label;
    // what it means: the step that loses the most people
    var worst = null;
    for (var i = 1; i < steps.length; i++) {
      var keep = steps[i - 1].value ? steps[i].value / steps[i - 1].value : 1;
      if (!worst || keep < worst.keep) worst = { i: i, keep: keep };
    }
    var leak = document.getElementById("leak");
    leak.textContent = worst && worst.keep < 1 ? "Biggest drop: " + steps[worst.i - 1].label + " to " + steps[worst.i].label + ", where " + board.fmt((1 - worst.keep) * 100, { style: "percent", decimals: 0 }) + " stop" : "";
    leak.hidden = !leak.textContent;
    el.innerHTML = steps.map(function (s, i) {
      var w = first.value ? Math.max(1.5, (s.value / first.value) * 100) : 0;
      var prev = steps[i - 1];
      var conv = prev && prev.value ? '<div class="conv' + (worst && worst.i === i ? " is-worst" : "") + '">' + board.fmt((s.value / prev.value) * 100, { style: "percent", decimals: 1 }) + " continue</div>" : "";
      return conv + '<div class="step cb-in" style="--i:' + i + '"><span class="name">' + board.esc(s.label) + '</span><span class="track"><i style="width:' + w.toFixed(1) + '%"></i></span><span class="cb-num">' + board.fmt(s.value) + "</span></div>";
    }).join("");
  });
</script>`;

const MIX = `<div class="cb-fill">
  <span class="cb-sub cb-clip" id="top" hidden></span>
  <div class="cb-chart" id="mix"></div>
</div>
<style>
  #top b { color: var(--fg); font-weight: 600; }
  [data-size="s"] #top { display: none; }
</style>
<script>
  board.onData(function (d) {
    // what it means: the part that matters most, and how much of the whole it is
    var sh = board.share(d.items), top = document.getElementById("top");
    top.innerHTML = sh && sh.parts.length > 1 ? "<b>" + board.esc(sh.top.label) + "</b> makes up " + board.fmt(sh.top.pct, { style: "percent", decimals: 0 }) + (sh.parts[1] ? ", then " + board.esc(sh.parts[1].label) + " at " + board.fmt(sh.parts[1].pct, { style: "percent", decimals: 0 }) : "") : "";
    top.hidden = !top.innerHTML;
    board.donut("#mix", d.items, { label: d.label || "Total", limit: 6 });
  });
</script>`;

const HEAT = `<div class="cb-fill">
  <div class="cb-split">
    <div class="cb-inline" style="--gap:0.62rem">
      <span class="cb-value is-sm" id="total">—</span>
      <span class="cb-label" id="what"></span>
    </div>
    <span class="cb-tag is-accent" id="streak" hidden></span>
  </div>
  <div class="cb-chart" id="heat"></div>
</div>
<script>
  board.onData(function (d) {
    var days = board.bucket(d.points, "day", "sum");
    var since = Date.now() - 365 * 864e5;
    var total = 0, on = {};
    days.forEach(function (p) {
      if (p.t >= since) total += p.v;
      if (p.v > 0) on[p.t] = true;
    });
    // days in a row with something on them, ending today (or yesterday, if today has not started)
    var day = new Date();
    day.setHours(0, 0, 0, 0);
    if (!on[day.getTime()]) day.setDate(day.getDate() - 1);
    var streak = 0;
    while (on[day.getTime()]) {
      streak++;
      day.setDate(day.getDate() - 1);
    }
    board.count("#total", total);
    // what it means: the day of the week most of it happens on
    var dow = [0, 0, 0, 0, 0, 0, 0];
    days.forEach(function (p) { if (p.t >= since) dow[new Date(p.t).getDay()] += p.v; });
    var busiest = dow.indexOf(Math.max.apply(null, dow));
    var NAMES = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];
    document.getElementById("what").textContent = (d.label ? d.label.toLowerCase() + " " : "") + "in the last year" + (total ? ", most on " + NAMES[busiest] : "");
    var tag = document.getElementById("streak");
    tag.hidden = streak < 2;
    tag.textContent = streak + "-day streak";
    board.heatmap("#heat", d.points, { none: "None" });
  });
</script>`;

const WEATHER = `<div class="cb-fill" id="main" hidden>
  <div class="art cb-grow" id="art"><svg id="sky" aria-hidden="true"></svg></div>
  <div class="cb-split" style="align-items:flex-end">
    <div class="now">
      <span class="cb-value is-md" id="temp"></span>
      <div class="cb-stack" style="--gap:0.08rem;min-width:0">
        <span class="cb-title cb-clip" id="what"></span>
        <span class="cb-sub cb-clip" id="feels"></span>
      </div>
    </div>
    <span class="cb-sub hilo" id="hilo"></span>
  </div>
</div>
<div class="cb-empty" id="empty"><b>No weather yet</b>It shows here once the weather source answers.</div>
<style>
  .art { min-height: 3.08rem; border-radius: var(--radius-sm); overflow: hidden; }
  .art svg { display: block; width: 100%; height: 100%; }
  .now { display: flex; align-items: flex-end; gap: 0.77rem; min-width: 0; }
  #temp { flex-shrink: 0; }
  .hilo { white-space: nowrap; font-variant-numeric: tabular-nums; }
  .sun { fill: var(--warn); }
  .moon, .flake { fill: var(--fg-2); }
  .star { fill: var(--fg-3); }
  html[data-mode="light"] .star { display: none; }
  .cloud { fill: color-mix(in srgb, var(--fg) 34%, transparent); }
  html[data-mode="light"] .cloud, html[data-mode="light"] .flake { fill: white; stroke: var(--line-2); stroke-width: 0.6; }
  /* the clouds cross the whole scene at one speed, however wide it is drawn */
  .drift { animation: drift var(--drift, 80s) linear infinite; }
  .fall { animation: fall 1.1s linear infinite; }
  .flake { animation: fall 7s linear infinite; }
  @keyframes drift { from { transform: translateX(-70px); } to { transform: translateX(var(--span, 230px)); } }
  @keyframes fall { from { transform: translateY(-12px); opacity: 0; } 30% { opacity: 1; } to { transform: translateY(36px); opacity: 0; } }
  [data-size="s"] .hilo, [data-size="s"] #feels { display: none; }
  @media (max-height: 130px) { .art { display: none; } }
</style>
<script>
  // what Open-Meteo's weather codes mean, by where each group starts
  var NAMES = [[0, "Clear"], [2, "Partly cloudy"], [3, "Overcast"], [45, "Fog"], [51, "Drizzle"], [61, "Rain"], [71, "Snow"], [80, "Showers"], [85, "Snow showers"], [95, "Thunderstorm"]];
  var tint = function (v, pct) { return "color-mix(in srgb, var(" + v + ") " + pct + "%, transparent)"; };
  var last = null;
  // the scene takes the shape of its box: at least 200 by 100, longer the long way, so the sun and the clouds
  // keep their size and nothing is cropped, in a wide tile or a tall one
  function frame() {
    var r = document.getElementById("art").getBoundingClientRect(), w = Math.max(1, r.width), h = Math.max(1, r.height);
    return w >= 2 * h ? { W: Math.round((100 * w) / h), H: 100 } : { W: 200, H: Math.round((200 * h) / w) };
  }
  function cloud(i, n, dur) {
    return '<g class="drift" style="animation-delay:-' + ((i * dur) / n).toFixed(1) + 's"><path transform="translate(0 ' + (12 + (i % 3) * 9) + ") scale(" + (1.1 + (i % 2) * 0.4) + ')" d="M0 14a8 8 0 0 1 8-8 11 11 0 0 1 21-1 9 9 0 0 1 10 9z" class="cloud"/></g>';
  }
  function draw(d) {
    var w = d.json, c = w && w.current, ok = !!c && typeof c.temp === "number";
    document.getElementById("main").hidden = !ok;
    document.getElementById("empty").hidden = ok;
    if (!ok) return;
    var name = "Clear";
    NAMES.forEach(function (n) { if (c.code >= n[0]) name = n[1]; });
    var snow = /Snow/.test(name), wet = !snow && /Drizzle|Rain|Showers|Thunder/.test(name);
    var f = frame(), W = f.W, H = f.H, more = (W * H) / 20000, sky = document.getElementById("sky");
    var dur = Math.round((80 * (W + 100)) / 300), clouds = Math.round((name === "Clear" ? 0 : name === "Partly cloudy" ? 2 : 4) * Math.max(1, W / 200));
    sky.setAttribute("viewBox", "0 0 " + W + " " + H);
    sky.style.setProperty("--span", W + 30 + "px");
    sky.style.setProperty("--drift", dur + "s");
    var s = '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color:' + tint("--accent", c.day ? 26 : 12) + '"/><stop offset="1" style="stop-color:' + tint("--accent", 3) + '"/></linearGradient></defs><rect width="' + W + '" height="' + H + '" style="fill:url(#g)"/>';
    for (var i = 0; !c.day && i < 16 * more; i++) s += '<circle cx="' + ((i * 53) % W) + '" cy="' + (5 + ((i * 29) % Math.round(H * 0.55))) + '" r=".7" class="star"/>';
    if (name === "Clear" || name === "Partly cloudy") {
      var sx = W - 50, sy = Math.min(32, H * 0.32);
      s += '<circle cx="' + sx + '" cy="' + sy + '" r="21" style="fill:' + tint(c.day ? "--warn" : "--fg", 14) + '"/><circle class="' + (c.day ? "sun" : "moon") + '" cx="' + sx + '" cy="' + sy + '" r="11"/>';
    }
    for (var k = 0; k < clouds; k++) s += cloud(k, clouds, dur);
    // rain and snow fill the sky below the clouds, however tall it is
    var low = Math.max(20, H - 64);
    for (var r = 0; wet && r < Math.min(140, 26 * more); r++) s += '<line class="fall" x1="' + ((r * 37) % W) + '" y1="' + (42 + ((r * 13) % low)) + '" x2="' + (((r * 37) % W) - 2) + '" y2="' + (48 + ((r * 13) % low)) + '" style="stroke:var(--fg-3);stroke-width:1.2;stroke-linecap:round;animation-delay:-' + ((r * 0.13) % 1.1).toFixed(2) + 's"/>';
    for (var fl = 0; snow && fl < Math.min(110, 20 * more); fl++) s += '<circle class="flake" cx="' + ((fl * 41) % W) + '" cy="' + (34 + ((fl * 7) % low)) + '" r="1.3" style="animation-delay:-' + ((fl * 0.39) % 7).toFixed(2) + 's"/>';
    s += '<path d="M0 ' + (H - 14) + " Q" + W / 4 + " " + (H - 28) + " " + W / 2 + " " + (H - 18) + " T" + W + " " + (H - 22) + " V" + H + ' H0Z" style="fill:' + tint("--fg", 9) + '"/>';
    sky.innerHTML = s;
    var today = (w.daily || [])[0];
    document.getElementById("temp").textContent = Math.round(c.temp) + "°";
    document.getElementById("what").textContent = name;
    // what it means for tomorrow: warmer or cooler, and whether rain is likely
    var next = (w.daily || [])[1], ahead = "";
    if (next && today && typeof next.hi === "number" && typeof today.hi === "number") {
      var dd = Math.round(next.hi - today.hi);
      ahead = "Tomorrow " + (dd > 0 ? dd + "° warmer" : dd < 0 ? -dd + "° cooler" : "the same") + (typeof next.rain === "number" && next.rain >= 50 ? ", rain likely" : "");
    }
    document.getElementById("feels").textContent = [typeof c.feels === "number" ? "Feels like " + Math.round(c.feels) + "°" : "", ahead].filter(Boolean).join(" · ");
    document.getElementById("hilo").textContent = today && typeof today.hi === "number" ? "H " + Math.round(today.hi) + "°  L " + Math.round(today.lo) + "°" : "";
  }
  board.onData(function (d) { last = d; draw(d); });
  // the scene follows its own box, which changes with the tile and with the words under it
  new ResizeObserver(function () { if (last) draw(last); }).observe(document.getElementById("art"));
</script>`;

const YEAR = `<div class="cb-fill">
  <div class="cb-split" style="align-items:flex-end">
    <div class="cb-stack" style="--gap:0.15rem">
      <span class="cb-value is-md" id="pct">—</span>
      <span class="cb-label" id="sub"></span>
    </div>
    <span class="cb-tag" id="week"></span>
  </div>
  <div class="cb-grow" id="dots"></div>
</div>
<style>
  #dots { display: grid; align-content: center; justify-content: center; }
  #dots i { display: block; border-radius: 50%; background: var(--line-2); }
  #dots i.past { background: color-mix(in srgb, var(--accent) 62%, transparent); }
  #dots i.today { background: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
</style>
<script>
  var dots = document.getElementById("dots");
  function draw() {
    var now = new Date(), y = now.getFullYear();
    var start = new Date(y, 0, 1), end = new Date(y + 1, 0, 1);
    var days = Math.round((end - start) / 864e5);
    var today = Math.round((new Date(y, now.getMonth(), now.getDate()) - start) / 864e5);
    var left = days - today - 1;
    var jan1 = start.getDay();
    document.getElementById("pct").textContent = board.fmt(((now - start) / (end - start)) * 100, { style: "percent", decimals: 1 });
    document.getElementById("sub").textContent = "of " + y + " gone, " + left + (left === 1 ? " day" : " days") + " to go";
    document.getElementById("week").textContent = "Week " + Math.ceil((today + jan1 + 1) / 7);
    // as many columns as keeps the dots biggest in the space there is
    var box = dots.getBoundingClientRect(), best = { c: 7, s: 0 };
    for (var c = 7; c <= 74; c++) {
      var s = Math.min(box.width / c, box.height / Math.ceil(days / c));
      if (s > best.s) best = { c: c, s: s };
    }
    var gap = Math.max(1.5, best.s * 0.3), size = Math.max(2, best.s - gap);
    dots.style.gridTemplateColumns = "repeat(" + best.c + ", " + size.toFixed(2) + "px)";
    dots.style.gap = gap.toFixed(2) + "px";
    var html = "";
    for (var i = 0; i < days; i++) html += '<i class="' + (i < today ? "past" : i === today ? "today" : "") + '" style="width:' + size.toFixed(2) + "px;height:" + size.toFixed(2) + 'px"></i>';
    dots.innerHTML = html;
  }
  draw();
  board.onResize(draw);
  setInterval(draw, 60000);
</script>`;

const SNAKE = `<div class="cb-fill snake">
  <div class="cb-split head">
    <div class="cb-inline" style="--gap:0.77rem;align-items:baseline">
      <span class="cb-value is-sm" id="score">0</span>
      <span class="cb-label" id="best"></span>
    </div>
    <span class="cb-label" id="keys">Arrows or WASD</span>
  </div>
  <div class="cb-grow pit" id="pit" tabindex="0" aria-label="Snake. Press Space to play, then steer with the arrow keys.">
    <canvas id="cv"></canvas>
    <div class="veil is-off" id="veil"><b id="title"></b><span id="hint"></span></div>
    <button class="cb-btn is-primary play" id="play">Play</button>
  </div>
</div>
<style>
  .snake { --gap: 0.62rem; }
  .pit { position: relative; min-height: 0; border-radius: var(--radius-sm, 10px); background: color-mix(in srgb, var(--field) 70%, transparent); overflow: hidden; cursor: pointer; outline: none; }
  .pit:focus-visible { box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 55%, transparent); }
  #cv { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
  .veil { position: absolute; inset: 0; display: grid; place-content: center; justify-items: center; text-align: center; gap: 0.31rem; padding-bottom: 2.6rem; background: color-mix(in srgb, rgb(var(--tile-rgb)) 55%, transparent); transition: opacity 0.2s ease; }
  .veil b { font-size: 1.31rem; font-weight: 650; color: var(--fg); }
  .veil span { font-size: 0.92rem; color: var(--fg-3); }
  .play { position: absolute; left: 50%; bottom: 0.9rem; transform: translateX(-50%); transition: opacity 0.2s ease; box-shadow: 0 0.4rem 1.2rem -0.5rem color-mix(in srgb, var(--accent) 70%, transparent); }
  .play:active { transform: translateX(-50%) scale(0.97); }
  .veil.is-off, .play.is-off { opacity: 0; pointer-events: none; }
  [data-size="s"] .head, [data-size="s"] .veil span { display: none; }
</style>
<script>
  var cv = document.getElementById("cv"), ctx = cv.getContext("2d"), pit = document.getElementById("pit");
  var veil = document.getElementById("veil"), title = document.getElementById("title"), hint = document.getElementById("hint"), play = document.getElementById("play");
  var scoreEl = document.getElementById("score"), bestEl = document.getElementById("best");
  var best = Number(board.state.best) || 0;
  var cols = 18, rows = 12, cell = 16, ox = 0, oy = 0, W = 0, H = 0;
  var snake = null, dir = { x: 1, y: 0 }, next = dir, food = null, score = 0, alive = false, paused = false, demo = false, timer = 0, step = 130;
  // for people who ask for less motion, the snake waits, still, until they play
  var still = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  var col = {};
  function css(n, f) { return getComputedStyle(document.documentElement).getPropertyValue(n).trim() || f; }
  // the theme's own colors, read again whenever the theme changes
  function colors() { col = { head: css("--accent", "gray"), body: css("--accent", "gray"), food: css("--warn", "gold"), check: css("--fg", "white"), eye: "rgb(" + css("--tile-rgb", "20 24 32") + ")" }; }
  function size() {
    var r = pit.getBoundingClientRect();
    W = Math.max(1, Math.floor(r.width)); H = Math.max(1, Math.floor(r.height));
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // square cells, as many as fit the pit, whatever shape the tile is
    cell = Math.max(8, Math.floor(Math.min(W / 12, H / 8, Math.sqrt((W * H) / 230))));
    cols = Math.max(8, Math.floor(W / cell)); rows = Math.max(6, Math.floor(H / cell));
    ox = Math.floor((W - cols * cell) / 2); oy = Math.floor((H - rows * cell) / 2);
    if (snake) {
      snake.forEach(function (p) { p.x = Math.min(p.x, cols - 1); p.y = Math.min(p.y, rows - 1); });
      if (food && (food.x >= cols || food.y >= rows)) place();
    }
    draw();
  }
  function place() {
    var free = [];
    for (var y = 0; y < rows; y++) for (var x = 0; x < cols; x++) if (!snake.some(function (p) { return p.x === x && p.y === y; })) free.push({ x: x, y: y });
    food = free[Math.floor(Math.random() * free.length)] || { x: 0, y: 0 };
  }
  function fresh() {
    var y = Math.floor(rows / 2), x = Math.max(2, Math.floor(cols / 3));
    snake = [{ x: x, y: y }, { x: x - 1, y: y }, { x: x - 2, y: y }];
    dir = next = { x: 1, y: 0 };
    place();
  }
  // before anyone plays, the snake plays itself: toward the food, never into a wall or itself
  function idle() {
    clearTimeout(timer);
    demo = true; alive = false; paused = false;
    fresh(); draw();
    if (!still && !document.hidden) timer = setTimeout(tick, 140);
  }
  function start() {
    clearTimeout(timer);
    demo = false; fresh();
    score = 0; step = 130; scoreEl.textContent = "0";
    alive = true; paused = false;
    veil.classList.add("is-off"); play.classList.add("is-off"); pit.focus();
    timer = setTimeout(tick, step);
  }
  function blocked(n) { return n.x < 0 || n.y < 0 || n.x >= cols || n.y >= rows || snake.some(function (p, i) { return i < snake.length - 1 && p.x === n.x && p.y === n.y; }); }
  function steer() {
    var h = snake[0], pick = null, near = Infinity;
    [dir, { x: dir.y, y: -dir.x }, { x: -dir.y, y: dir.x }].forEach(function (o) {
      var n = { x: h.x + o.x, y: h.y + o.y };
      if (blocked(n)) return;
      var d = Math.abs(n.x - food.x) + Math.abs(n.y - food.y);
      if (d < near) { near = d; pick = o; }
    });
    if (pick) next = pick;
  }
  function tick() {
    if (!(alive || demo) || paused) return;
    if (demo) steer();
    dir = next;
    var h = snake[0], n = { x: h.x + dir.x, y: h.y + dir.y };
    if (blocked(n)) return demo ? idle() : over();
    snake.unshift(n);
    if (n.x === food.x && n.y === food.y) {
      if (!demo) { score++; scoreEl.textContent = String(score); step = Math.max(62, 130 - score * 3); }
      place();
      // the snake that plays itself stays short
      if (demo && snake.length > 12) snake.pop();
    } else snake.pop();
    draw();
    timer = setTimeout(tick, demo ? 140 : step);
  }
  function over() {
    alive = false;
    var record = score > best;
    if (record) { best = score; board.save({ best: best }); }
    showBest();
    title.textContent = score === 1 ? "1 point" : score + " points";
    hint.textContent = record && score > 1 ? "A new best" : best ? "Your best is " + best : "Eat the glowing dots to grow";
    play.textContent = "Play again";
    veil.classList.remove("is-off"); play.classList.remove("is-off");
    draw();
  }
  function pause(on) {
    if (!alive) return;
    paused = on;
    title.textContent = "Paused"; hint.textContent = "Click or press Space to go on";
    veil.classList.toggle("is-off", !on);
    // one game loop, always: the step still waiting goes before another starts
    clearTimeout(timer);
    if (!on) timer = setTimeout(tick, step);
  }
  function turn(x, y) { if (alive && !paused && (x !== -dir.x || y !== -dir.y)) next = { x: x, y: y }; }
  function showBest() { bestEl.textContent = best ? "best " + best : ""; }
  function piece(x, y, pad, r) {
    var px = ox + x * cell + pad, py = oy + y * cell + pad, s = cell - pad * 2;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(px, py, s, s, r); else ctx.rect(px, py, s, s);
  }
  function draw() {
    ctx.clearRect(0, 0, W, H);
    // a quiet checkerboard to steer by
    ctx.fillStyle = col.check; ctx.globalAlpha = 0.035;
    for (var y = 0; y < rows; y++) for (var x = y % 2; x < cols; x += 2) ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
    ctx.globalAlpha = 1;
    if (!snake) return;
    // the food glows
    ctx.save(); ctx.shadowColor = col.food; ctx.shadowBlur = cell * 0.9; ctx.fillStyle = col.food;
    ctx.beginPath(); ctx.arc(ox + food.x * cell + cell / 2, oy + food.y * cell + cell / 2, cell * 0.28, 0, 7); ctx.fill(); ctx.restore();
    // the snake: rounded pieces with a gap between, its head in the accent and its body fading toward the tail
    var pad = Math.max(1, cell * 0.09);
    for (var i = snake.length - 1; i >= 0; i--) {
      var k = snake.length > 1 ? i / (snake.length - 1) : 0;
      ctx.globalAlpha = (demo ? 0.8 : 1) * (i === 0 ? 1 : 0.72 - k * 0.37);
      ctx.fillStyle = i === 0 ? col.head : col.body;
      piece(snake[i].x, snake[i].y, pad, cell * 0.3);
      ctx.fill();
    }
    // and its eyes look the way it is going
    var h = snake[0], cx = ox + h.x * cell + cell / 2 + dir.x * cell * 0.12, cy = oy + h.y * cell + cell / 2 + dir.y * cell * 0.12;
    ctx.globalAlpha = 1; ctx.fillStyle = col.eye;
    [-1, 1].forEach(function (side) {
      ctx.beginPath();
      ctx.arc(cx - dir.y * cell * 0.17 * side, cy + dir.x * cell * 0.17 * side, Math.max(1, cell * 0.07), 0, 7);
      ctx.fill();
    });
  }
  var KEYS = { ArrowUp: [0, -1], w: [0, -1], ArrowDown: [0, 1], s: [0, 1], ArrowLeft: [-1, 0], a: [-1, 0], ArrowRight: [1, 0], d: [1, 0] };
  // the keys it uses are its own: preventDefault keeps the board from acting on them
  window.addEventListener("keydown", function (e) {
    var k = KEYS[e.key.length === 1 ? e.key.toLowerCase() : e.key];
    if (k) { e.preventDefault(); if (demo) start(); turn(k[0], k[1]); }
    else if (e.key === " " || e.key === "Enter") { e.preventDefault(); if (alive) pause(!paused); else start(); }
  });
  play.addEventListener("click", function (e) { e.stopPropagation(); start(); });
  pit.addEventListener("click", function () { if (alive) pause(!paused); else start(); });
  // a swipe turns it on a phone
  var touch = null;
  pit.addEventListener("touchstart", function (e) { touch = e.touches[0]; }, { passive: true });
  pit.addEventListener("touchend", function (e) {
    if (!touch) return;
    var t = e.changedTouches[0], dx = t.clientX - touch.clientX, dy = t.clientY - touch.clientY;
    if (Math.max(Math.abs(dx), Math.abs(dy)) > 18) { if (Math.abs(dx) > Math.abs(dy)) turn(dx > 0 ? 1 : -1, 0); else turn(0, dy > 0 ? 1 : -1); }
    touch = null;
  });
  // out of sight, the game waits, and so does the snake that plays itself
  document.addEventListener("visibilitychange", function () {
    if (document.hidden) { if (alive) pause(true); else clearTimeout(timer); }
    else if (demo && !still) { clearTimeout(timer); timer = setTimeout(tick, 140); }
  });
  colors(); showBest(); size(); idle();
  board.onResize(size);
  board.onTheme(function () { colors(); draw(); });
</script>`;

const CALENDAR = `<div class="cb-fill cal">
  <div class="cb-split">
    <div class="cb-inline" style="--gap:0.62rem;align-items:baseline;min-width:0">
      <span class="cb-display cb-clip" id="month"></span>
      <span class="cb-sub" id="year"></span>
    </div>
    <div class="cb-inline nav" style="--gap:0.15rem">
      <button class="cb-btn is-quiet is-round" id="prev" aria-label="Previous month">‹</button>
      <button class="cb-btn is-quiet" id="now">Today</button>
      <button class="cb-btn is-quiet is-round" id="next" aria-label="Next month">›</button>
    </div>
  </div>
  <div class="body">
    <div class="grid" id="grid"></div>
    <div class="agenda" id="agenda"></div>
    <div class="big"><span class="cb-value" id="big-day"></span><span class="cb-label" id="big-name"></span></div>
  </div>
  <span class="cb-sub cb-clip line" id="line"></span>
</div>
<style>
  .cal { --gap: 0.77rem; }
  .body { flex: 1; min-height: 0; display: flex; gap: 1.23rem; }
  .grid { flex: 1; min-width: 0; display: grid; grid-template-columns: repeat(7, 1fr); grid-auto-rows: 1fr; gap: 0.23rem; }
  .dow { display: grid; place-items: center; font-size: 0.81rem; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--fg-4); }
  .day { position: relative; display: grid; place-items: center; min-height: 0; border: 0; padding: 0; border-radius: 0.69rem; background: transparent; color: var(--fg-2); font: inherit; font-size: 0.96rem; font-variant-numeric: tabular-nums; cursor: pointer; transition: background 0.15s ease, color 0.15s ease; }
  .day:hover { background: var(--field); color: var(--fg); }
  .day.is-out { color: var(--fg-4); opacity: 0.45; }
  .day .n { display: grid; place-items: center; width: 1.9em; height: 1.9em; border-radius: 50%; font-weight: inherit; }
  .day.is-today .n { background: var(--accent); color: var(--accent-fg); font-weight: 650; }
  .day.is-on { background: color-mix(in srgb, var(--accent) 16%, transparent); color: var(--fg); }
  .dots { position: absolute; bottom: 0.23rem; left: 0; right: 0; display: flex; justify-content: center; gap: 0.15rem; }
  .dots i { width: 0.31rem; height: 0.31rem; border-radius: 50%; }
  .agenda { width: 42%; min-width: 0; display: none; flex-direction: column; gap: 0.46rem; overflow: auto; }
  .agenda h4 { margin: 0 0 0.15rem; font-size: 0.92rem; font-weight: 600; color: var(--fg-2); }
  .ev { display: flex; gap: 0.62rem; align-items: baseline; padding: 0.54rem 0.69rem; border-radius: 0.69rem; background: var(--field); }
  .ev time { font-size: 0.85rem; color: var(--fg-3); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .ev b { font-size: 0.96rem; font-weight: 560; color: var(--fg); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .none { font-size: 0.92rem; color: var(--fg-3); }
  [data-size="l"] .agenda { display: flex; }
  [data-size="l"] .line { display: none; }
  .big { display: none; flex-direction: column; justify-content: center; gap: 0.15rem; }
  [data-size="s"] .grid, [data-size="s"] .nav { display: none; }
  [data-size="s"] .big { display: flex; }
</style>
<script>
  var MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  // the week starts where the user's own calendar starts it
  var first = 0;
  try { var wi = new Intl.Locale(navigator.language).weekInfo || new Intl.Locale(navigator.language).getWeekInfo(); first = wi.firstDay % 7; } catch (e) { first = 0; }
  var shown = new Date(), picked = null, events = {};
  shown.setDate(1);
  function key(d) { return d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate(); }
  // an all-day event is a date, not a moment: a calendar source sends its day as midnight UTC, so it is that date wherever the user is
  function when(e) {
    var t = e.t != null ? e.t : e.value, d = new Date(typeof t === "number" ? t : Date.parse(t));
    if (isNaN(d)) return null;
    return e.value === "all-day" ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) : d;
  }
  // a timed event at midnight is at 12:00 AM: only an all-day event is all day
  function timeOf(e) { return e.allDay ? "All day" : e.when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); }
  function dayName(d) { return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }); }
  function draw() {
    var y = shown.getFullYear(), m = shown.getMonth(), today = new Date();
    document.getElementById("month").textContent = MONTHS[m];
    document.getElementById("year").textContent = y;
    var lead = (new Date(y, m, 1).getDay() - first + 7) % 7;
    var start = new Date(y, m, 1 - lead);
    var html = "";
    for (var i = 0; i < 7; i++) html += '<span class="dow">' + new Date(2026, 0, 4 + ((first + i) % 7)).toLocaleDateString("en-US", { weekday: "narrow" }) + "</span>";
    for (var j = 0; j < 42; j++) {
      var d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + j), k = key(d), list = events[k] || [];
      var cls = "day" + (d.getMonth() !== m ? " is-out" : "") + (k === key(today) ? " is-today" : "") + (picked && k === key(picked) ? " is-on" : "");
      var dots = list.slice(0, 3).map(function (_, n) { return '<i style="background:var(--chart-' + (n + 1) + ')"></i>'; }).join("");
      html += '<button class="' + cls + '" data-k="' + k + '" aria-label="' + board.esc(dayName(d) + (list.length ? ", " + list.length + (list.length === 1 ? " event" : " events") : "")) + '"><b class="n">' + d.getDate() + "</b>" + (dots ? '<span class="dots">' + dots + "</span>" : "") + "</button>";
    }
    document.getElementById("grid").innerHTML = html;
    document.getElementById("big-day").textContent = today.getDate();
    document.getElementById("big-name").textContent = today.toLocaleDateString("en-US", { weekday: "long" });
    agenda();
  }
  // when an event is over: a timed one an hour after it starts, an all-day one at the next midnight
  // (a day can be 23 or 25 hours long when the clocks change, so never "plus 24 hours")
  function over(e) { var d = e.when; return e.allDay ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() : d.getTime() + 3600000; }
  function upcoming() {
    var now = Date.now(), out = [];
    for (var k in events) events[k].forEach(function (e) { if (over(e) > now) out.push(e); });
    // in the order they happen, an all-day event first on its day
    return out.sort(function (a, b) { return a.when - b.when || (b.allDay ? 1 : 0) - (a.allDay ? 1 : 0); });
  }
  function agenda() {
    var day = picked || new Date(), list = (events[key(day)] || []).slice().sort(function (a, b) { return a.when - b.when || (b.allDay ? 1 : 0) - (a.allDay ? 1 : 0); });
    var connected = !!(board.data && board.data.items);
    var html = "<h4>" + board.esc(dayName(day)) + "</h4>";
    html += list.length ? list.map(function (e) { return '<div class="ev"><time>' + timeOf(e) + "</time><b>" + board.esc(e.label) + "</b></div>"; }).join("") : '<span class="none">' + (connected ? "Nothing on this day." : "Connect a calendar to see its events here.") + "</span>";
    document.getElementById("agenda").innerHTML = html;
    var next = upcoming()[0];
    document.getElementById("line").textContent = !connected ? "Connect a calendar to see its events here" : next ? "Next: " + next.label + ", " + (key(next.when) === key(new Date()) ? "today" : next.when.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })) + " · " + timeOf(next) : "Nothing coming up";
  }
  document.getElementById("grid").addEventListener("click", function (e) {
    var b = e.target.closest(".day");
    if (!b) return;
    var p = b.dataset.k.split("-");
    picked = new Date(+p[0], +p[1], +p[2]);
    if (picked.getMonth() !== shown.getMonth()) shown = new Date(picked.getFullYear(), picked.getMonth(), 1);
    draw();
  });
  document.getElementById("prev").onclick = function () { shown = new Date(shown.getFullYear(), shown.getMonth() - 1, 1); draw(); };
  document.getElementById("next").onclick = function () { shown = new Date(shown.getFullYear(), shown.getMonth() + 1, 1); draw(); };
  document.getElementById("now").onclick = function () { shown = new Date(); shown.setDate(1); picked = null; draw(); };
  board.onData(function (d) {
    events = {};
    (d.items || []).forEach(function (it) {
      var w = when(it);
      if (!w) return;
      (events[key(w)] = events[key(w)] || []).push({ when: w, allDay: it.value === "all-day", label: it.label || "Event", sub: it.sub || "" });
    });
    draw();
  });
  draw();
</script>`;

const FOCUS = `<div class="cb-fill">
  <div class="cb-grow" id="ring"></div>
  <div class="cb-split">
    <div class="cb-inline" style="--gap:0.46rem">
      <button class="cb-btn is-primary" id="go">Start</button>
      <button class="cb-btn is-quiet" id="reset">Reset</button>
    </div>
    <span class="cb-label" id="count"></span>
  </div>
</div>
<script>
  var FOCUS = 25 * 60, REST = 5 * 60;
  var go = document.getElementById("go"), reset = document.getElementById("reset");
  function today() { return new Date().toDateString(); }
  var s = Object.assign({ mode: "focus", left: FOCUS, endsAt: null, day: today(), done: 0 }, board.state);
  if (s.day !== today()) { s.day = today(); s.done = 0; }
  function full() { return s.mode === "focus" ? FOCUS : REST; }
  function left() { return s.endsAt ? Math.max(0, Math.round((s.endsAt - Date.now()) / 1000)) : s.left; }
  function clock(n) { var m = Math.floor(n / 60), x = n % 60; return m + ":" + (x < 10 ? "0" : "") + x; }
  function finish() {
    if (s.mode === "focus") s.done++;
    s.mode = s.mode === "focus" ? "rest" : "focus";
    s.left = full();
    s.endsAt = null;
    board.save(s);
  }
  function draw() {
    if (s.endsAt && left() === 0) finish();
    var l = left();
    board.ring("#ring", 1 - l / full(), { label: clock(l), sub: s.mode === "focus" ? "Focus" : "Break", color: s.mode === "focus" ? "var(--accent)" : "var(--ok)" });
    go.textContent = s.endsAt ? "Pause" : l < full() ? "Resume" : "Start";
    document.getElementById("count").textContent = s.done ? s.done + (s.done === 1 ? " session" : " sessions") + " today" : "None yet today";
  }
  go.onclick = function () {
    if (s.endsAt) { s.left = left(); s.endsAt = null; } else s.endsAt = Date.now() + left() * 1000;
    board.save(s);
    draw();
  };
  reset.onclick = function () { s.endsAt = null; s.left = full(); board.save(s); draw(); };
  go.disabled = reset.disabled = board.readOnly;
  draw();
  setInterval(draw, 1000);
</script>`;

const MOON = `<div class="moon">
  <svg id="art" viewBox="0 0 100 100" aria-hidden="true">
    <defs><radialGradient id="lit" cx="40%" cy="38%" r="70%"><stop offset="0" style="stop-color:var(--fg)"/><stop offset="1" style="stop-color:color-mix(in srgb, var(--fg) 72%, var(--accent))"/></radialGradient></defs>
    <circle cx="50" cy="50" r="44" style="fill:color-mix(in srgb, var(--fg) 8%, transparent);stroke:color-mix(in srgb, var(--fg) 16%, transparent);stroke-width:0.6"/>
    <path id="lit-part" style="fill:url(#lit)"/>
  </svg>
  <div class="cb-stack" style="--gap:0.31rem">
    <span class="cb-display" id="name">—</span>
    <span class="cb-label" id="lit-pct"></span>
    <span class="cb-sub" id="next"></span>
  </div>
</div>
<style>
  .moon { height: 100%; display: flex; align-items: center; gap: 1.23rem; }
  #art { height: min(100%, 60vw); aspect-ratio: 1; flex-shrink: 0; filter: drop-shadow(0 0 14px color-mix(in srgb, var(--accent) 22%, transparent)); }
  [data-shape="tall"] .moon { flex-direction: column; justify-content: center; text-align: center; }
  [data-shape="tall"] #art { height: auto; width: min(100%, 52vh); }
</style>
<script>
  var MONTH = 29.530588853, NEW = Date.UTC(2000, 0, 6, 18, 14);
  var NAMES = ["New moon", "Waxing crescent", "First quarter", "Waxing gibbous", "Full moon", "Waning gibbous", "Last quarter", "Waning crescent", "New moon"];
  function draw() {
    var age = (((Date.now() - NEW) / 864e5) % MONTH + MONTH) % MONTH;
    var phase = age / MONTH;
    var k = Math.cos(phase * 2 * Math.PI);
    var rx = Math.abs(k) * 44, waxing = phase < 0.5;
    var outer = waxing ? 1 : 0, inner = waxing ? (k > 0 ? 0 : 1) : k > 0 ? 1 : 0;
    document.getElementById("lit-part").setAttribute("d", "M50 6 A44 44 0 0 " + outer + " 50 94 A" + rx.toFixed(2) + " 44 0 0 " + inner + " 50 6Z");
    var name = NAMES[Math.round(phase * 8)];
    document.getElementById("name").textContent = name;
    document.getElementById("lit-pct").textContent = Math.round(((1 - k) / 2) * 100) + "% lit tonight";
    // the next full or new moon, whichever the name above is not
    var toFull = (MONTH / 2 - age + MONTH) % MONTH, toNew = MONTH - age;
    var soon = name === "Full moon" ? ["New moon", toNew] : name === "New moon" ? ["Full moon", toFull] : toFull < toNew ? ["Full moon", toFull] : ["New moon", toNew];
    var days = Math.round(soon[1]);
    document.getElementById("next").textContent = days < 1 ? soon[0] + " tonight" : days === 1 ? soon[0] + " tomorrow" : soon[0] + " in about " + days + " days";
  }
  draw();
  setInterval(draw, 600000);
</script>`;

const UPTIME = `<div class="cb-fill" id="main" hidden>
  <div class="cb-split top">
    <span class="cb-label cb-clip" id="span"></span>
    <span class="cb-tag" id="now" hidden></span>
  </div>
  <div class="rows" id="rows"></div>
  <span class="cb-sub cb-clip" id="line"></span>
</div>
<div class="cb-empty" id="empty"><b>No checks yet</b>Each service's days show here once the monitor reports them.</div>
<style>
  .rows { flex: 1; min-height: 0; display: flex; flex-direction: column; justify-content: space-evenly; }
  .svc { display: grid; grid-template-columns: var(--name, 5rem) minmax(0, 1fr) 4.4rem; align-items: center; gap: 0.8rem; }
  .svc-name { display: flex; align-items: center; gap: 0.5rem; min-width: 0; font-size: 0.95rem; color: var(--fg-2); }
  .svc-name span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .days { display: flex; gap: 2px; height: var(--bar-h, 1.5rem); min-width: 0; }
  .days i { flex: 1 1 0; min-width: 0; border-radius: 99px; background: var(--ok); opacity: 0.78; transform-origin: bottom; animation: rise 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both; animation-delay: calc(var(--i, 0) * 6ms); }
  .days i.is-warn { background: var(--warn); opacity: 1; }
  .days i.is-bad { background: var(--bad); opacity: 1; }
  .days i.is-none { background: var(--line-2); opacity: 1; }
  .days i:hover { opacity: 1; }
  .svc .cb-num { font-size: 0.95rem; text-align: right; color: var(--fg); }
  @keyframes rise { from { transform: scaleY(0.25); opacity: 0; } }
  [data-size="s"] .top, [data-size="s"] #line { display: none; }
</style>
<script>
  var last = null;
  // a day is good at 99.9% up or more, worth a look under that, and bad under 99%
  function kind(v) { return typeof v !== "number" ? "is-none" : v >= 99.9 ? "" : v >= 99 ? "is-warn" : "is-bad"; }
  // short of 100% never reads as 100%
  function pct(v) { return v >= 100 ? "100%" : (Math.floor(v * 100) / 100).toFixed(2) + "%"; }
  function dayName(back) { var d = new Date(); d.setDate(d.getDate() - back); return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  function draw(d) {
    var list = ((d.json && d.json.services) || []).filter(function (s) { return s && Array.isArray(s.days) && s.days.length; });
    var ok = list.length > 0;
    document.getElementById("main").hidden = !ok;
    document.getElementById("empty").hidden = ok;
    if (!ok) return;
    var rows = document.getElementById("rows");
    // the names' column is as wide as the longest name, within reason
    var longest = Math.max.apply(null, list.map(function (s) { return String(s.name || "").length; }));
    rows.style.setProperty("--name", Math.min(11, Math.max(3.5, longest * 0.58 + 1.4)).toFixed(1) + "rem");
    rows.innerHTML = list.map(function () { return '<div class="svc"><span class="svc-name"><i class="cb-dot"></i><span></span></span><span class="days"></span><span class="cb-num"></span></div>'; }).join("");
    // as many days as fit at 3px a bar with 2px between, and bars as tall as their rows allow
    var strip = rows.querySelector(".days").getBoundingClientRect();
    var most = Math.max.apply(null, list.map(function (s) { return s.days.length; }));
    var n = Math.max(1, Math.min(most, Math.floor((strip.width + 2) / 5)));
    var rowH = rows.getBoundingClientRect().height / list.length;
    rows.style.setProperty("--bar-h", Math.max(14, Math.min(rowH * 0.62, 44)).toFixed(0) + "px");
    var worst = null, down = [];
    [].forEach.call(rows.children, function (el, k) {
      var s = list[k], days = s.days.slice(-n), had = days.filter(function (v) { return typeof v === "number"; });
      var up = had.length ? had.reduce(function (a, v) { return a + v; }, 0) / had.length : null;
      // the dot says how it is now: the monitor's own word when it sends one, else its last day
      var end = days[days.length - 1], now = s.now || (typeof end === "number" ? (end >= 99 ? "up" : "down") : null);
      if (now === "down") down.push(s.name || "A service");
      el.querySelector(".cb-dot").className = "cb-dot " + (now === "down" ? "is-bad" : now === "slow" ? "is-warn" : now ? "is-ok" : "");
      el.querySelector(".svc-name span").textContent = s.name || "Service";
      el.querySelector(".cb-num").textContent = up === null ? "—" : pct(up);
      el.querySelector(".days").innerHTML = days.map(function (v, i) {
        var back = days.length - 1 - i;
        if (typeof v === "number" && (!worst || v < worst.v)) worst = { v: v, name: s.name || "A service", back: back };
        return '<i class="' + kind(v) + '" style="--i:' + i + '" title="' + board.esc(dayName(back) + ": " + (typeof v === "number" ? pct(v) + " up" : "no data")) + '"></i>';
      }).join("");
    });
    document.getElementById("span").textContent = n === 1 ? "Today" : "Last " + n + " days";
    var tag = document.getElementById("now");
    tag.hidden = !list.some(function (s) { return s.now; });
    tag.className = "cb-tag " + (down.length ? "is-bad" : "is-ok");
    tag.textContent = down.length ? (down.length === 1 ? down[0] + " is down" : down.length + " are down") : "All up";
    // what it means: the worst day in sight, or that there was none
    document.getElementById("line").textContent = !worst ? "" : worst.v >= 99.9 ? "Not one bad day in " + n : worst.name + "'s worst day was " + dayName(worst.back) + ", at " + pct(worst.v) + " up";
  }
  board.onData(function (d) { last = d; draw(d); });
  board.onResize(function () { if (last) draw(last); });
</script>`;

const SCORES = `<div class="cb-fill" id="main" hidden>
  <div class="rings" id="rings"></div>
  <span class="cb-sub cb-clip" id="line"></span>
</div>
<div class="cb-empty" id="empty"><b>No scores yet</b>They show here after the first audit.</div>
<style>
  .rings { flex: 1; min-height: 0; display: grid; align-content: space-evenly; justify-content: space-evenly; gap: 0.8rem 0.5rem; }
  .score { display: flex; flex-direction: column; align-items: center; gap: 0.45rem; min-width: 0; }
  .ring { position: relative; }
  .ring svg { display: block; width: 100%; height: 100%; transform: rotate(-90deg); overflow: visible; }
  .ring .trk { fill: none; stroke: var(--line-2); }
  .ring .val { fill: none; stroke: currentColor; stroke-linecap: round; transition: stroke-dasharray 1.1s cubic-bezier(0.2, 0.9, 0.3, 1); filter: drop-shadow(0 0 3px color-mix(in srgb, currentColor 50%, transparent)); }
  .ring b { position: absolute; inset: 0; display: grid; place-items: center; font-family: var(--font-num); font-weight: var(--num-weight); font-variant-numeric: tabular-nums; color: var(--fg); }
  .score .cb-label { white-space: nowrap; }
  .is-good { color: var(--ok); }
  .is-fair { color: var(--warn); }
  .is-poor { color: var(--bad); }
  [data-size="s"] #line, [data-size="s"] .score .cb-label { display: none; }
</style>
<script>
  var last = null, shown = {};
  // Lighthouse's own bands: 90 and up is good, 50 to 89 needs work, under 50 is poor
  function band(v) { return v >= 90 ? "is-good" : v >= 50 ? "is-fair" : "is-poor"; }
  function draw(d) {
    var items = (d.items || []).filter(function (i) { return typeof i.value === "number"; });
    var ok = items.length > 0;
    document.getElementById("main").hidden = !ok;
    document.getElementById("empty").hidden = ok;
    if (!ok) return;
    // PageSpeed Insights scores from 0 to 1, and the rings read 0 to 100
    var k = items.every(function (i) { return i.value <= 1; }) ? 100 : 1;
    var list = items.map(function (i) { return { label: i.label || "Score", v: Math.max(0, Math.min(100, Math.round(i.value * k))) }; });
    var el = document.getElementById("rings"), box = el.getBoundingClientRect();
    var rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 13;
    // in a small tile the rings stand alone; otherwise each has its name under it
    var named = document.documentElement.getAttribute("data-size") !== "s";
    // as many to a row as makes the rings biggest, with room for the names
    var best = { cols: 1, size: 0 };
    for (var c = 1; c <= list.length; c++) {
      var r = Math.ceil(list.length / c);
      var size = Math.min(((box.width - (c - 1) * rem * 0.5) / c) * 0.8, (box.height - (r - 1) * rem * 0.8) / r - (named ? rem * 1.9 : 0));
      if (size > best.size) best = { cols: c, size: size };
    }
    var s = Math.max(28, Math.min(150, Math.floor(best.size)));
    el.style.gridTemplateColumns = "repeat(" + best.cols + ", " + Math.max(s, Math.min(rem * 4.5, box.width / best.cols - rem * 0.5)).toFixed(0) + "px)";
    el.innerHTML = list.map(function (x, i) {
      return '<div class="score cb-in" style="--i:' + i + '"><div class="ring ' + band(x.v) + '" style="width:' + s + "px;height:" + s + 'px"><svg viewBox="0 0 40 40"><circle class="trk" cx="20" cy="20" r="16" stroke-width="3.2"/><circle class="val" cx="20" cy="20" r="16" stroke-width="3.2" pathLength="100" style="stroke-dasharray:' + (shown[x.label] || 0) + ' 100"/></svg><b style="font-size:' + (s * (x.v > 99 ? 0.27 : 0.32)).toFixed(1) + 'px">' + x.v + '</b></div><span class="cb-label">' + board.esc(x.label) + "</span></div>";
    }).join("");
    // each ring fills from where it was to where it is now
    requestAnimationFrame(function () {
      [].forEach.call(el.querySelectorAll(".val"), function (ring, i) { ring.style.strokeDasharray = list[i].v + " 100"; shown[list[i].label] = list[i].v; });
    });
    // what it means: the one to work on, or that there is none
    var low = list.slice().sort(function (a, b) { return a.v - b.v; })[0];
    document.getElementById("line").textContent = low.v >= 90 ? (list.length === 1 ? "In the green" : list.length === 2 ? "Both in the green" : "All " + list.length + " in the green") : low.label + " is the one to work on, at " + low.v;
  }
  board.onData(function (d) { last = d; draw(d); });
  board.onResize(function () { if (last) draw(last); });
</script>`;

const POSTS = `<div class="cb-fill" id="main" hidden>
  <div class="cb-inline top" style="--gap:0.5rem;align-items:baseline;flex-wrap:nowrap">
    <span class="cb-value is-sm" id="total"></span>
    <span class="cb-label cb-clip" id="what"></span>
  </div>
  <div class="posts cb-scroll" id="posts"></div>
</div>
<div class="cb-empty" id="empty"><b>No posts yet</b>Your latest posts show here once the feed sends them.</div>
<style>
  .posts { flex: 1; display: flex; flex-direction: column; }
  .post { display: flex; gap: 0.75rem; padding: 0.7rem 0; border-top: 1px solid var(--line); color: inherit; text-decoration: none; }
  .post:first-child { border-top: 0; padding-top: 0.15rem; }
  a.post:hover p { color: var(--fg); }
  .mark { flex-shrink: 0; width: 2rem; height: 2rem; border-radius: 50%; display: grid; place-items: center; background: var(--fg); color: rgb(var(--tile-rgb)); font-size: 0.85rem; font-weight: 700; }
  .mark svg { width: 0.9rem; height: 0.9rem; }
  .body { min-width: 0; display: flex; flex-direction: column; gap: 0.3rem; }
  .post p { margin: 0; font-size: 1rem; line-height: 1.4; color: var(--fg-2); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .meta { display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap; font-size: 0.88rem; color: var(--fg-3); font-variant-numeric: tabular-nums; }
  .meta b { color: var(--fg); font-weight: 650; }
  .meta i { width: 0.2rem; height: 0.2rem; border-radius: 50%; background: var(--fg-4); }
  .post.is-top .meta b { color: var(--accent); }
  [data-size="s"] .top { display: none; }
</style>
<script>
  // the source's mark: X's for a post on X, else the first letter of its site
  var X = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4l14 16M19 4L5 20" style="stroke:currentColor;stroke-width:2.8;stroke-linecap:round"/></svg>';
  function mark(href) {
    var host = "";
    try { host = new URL(href).hostname.replace(/^www[.]/, ""); } catch (e) {}
    return host === "x.com" || host === "twitter.com" ? X : board.esc((host || "?").charAt(0).toUpperCase());
  }
  board.onData(function (d) {
    var posts = (d.items || []).filter(function (p) { return p && p.label; });
    var ok = posts.length > 0;
    document.getElementById("main").hidden = !ok;
    document.getElementById("empty").hidden = ok;
    if (!ok) return;
    var counted = posts.filter(function (p) { return typeof p.value === "number"; });
    var total = counted.reduce(function (a, p) { return a + p.value; }, 0);
    // what it means: the one that did best, picked out
    var top = counted.length > 1 ? counted.slice().sort(function (a, b) { return b.value - a.value; })[0] : null;
    board.count("#total", total, { format: "compact" });
    document.getElementById("what").textContent = "views on the last " + (posts.length === 1 ? "post" : posts.length + " posts") + (top ? ", the best with " + board.fmt(top.value, "compact") : "");
    document.getElementById("posts").innerHTML = posts.map(function (p, i) {
      var meta = [typeof p.value === "number" ? "<b>" + board.fmt(p.value, "compact") + "</b> views" : "", p.sub ? board.esc(p.sub) : "", p.t ? board.esc(board.ago(p.t)) : ""].filter(Boolean).join("<i></i>");
      var link = p.href ? ' href="' + board.esc(p.href) + '"' : "";
      // a post with a link opens it, in a new tab
      return "<" + (link ? "a" : "div") + ' class="post cb-in' + (p === top ? " is-top" : "") + '" style="--i:' + i + '"' + link + '><span class="mark">' + mark(p.href) + '</span><div class="body"><p>' + board.esc(p.label) + '</p><span class="meta">' + meta + "</span></div></" + (link ? "a" : "div") + ">";
    }).join("");
  });
</script>`;

const MAP = `<div class="cb-fill" id="main" hidden>
  <div class="cb-inline top" style="--gap:0.5rem;align-items:baseline;flex-wrap:nowrap">
    <span class="cb-value is-sm" id="total"></span>
    <span class="cb-label cb-clip" id="what"></span>
  </div>
  <div class="map cb-grow" id="map">
    <canvas id="cv" aria-hidden="true"></canvas>
    <div class="pins" id="pins"></div>
    <ol class="top3 cb-card" id="top3"></ol>
  </div>
</div>
<div class="cb-empty" id="empty"><b>Nobody on the map yet</b>Places show here as the stream sends them, by country or by latitude and longitude.</div>
<style>
  .map { position: relative; min-height: 0; }
  #cv { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
  .pins { position: absolute; inset: 0; pointer-events: none; }
  .pin { position: absolute; width: var(--d); height: var(--d); margin: calc(var(--d) / -2) 0 0 calc(var(--d) / -2); border-radius: 50%; background: var(--accent); box-shadow: 0 0 0.6rem color-mix(in srgb, var(--accent) 70%, transparent); pointer-events: auto; }
  .pin::after { content: ""; position: absolute; inset: -0.2rem; border-radius: 50%; border: 1.5px solid var(--accent); opacity: 0; animation: ping 2.4s cubic-bezier(0.2, 0.7, 0.3, 1) infinite; animation-delay: var(--delay, 0s); }
  @keyframes ping { 0% { transform: scale(0.6); opacity: 0.7; } 100% { transform: scale(2.6); opacity: 0; } }
  .top3 { position: absolute; right: 0; bottom: 0; margin: 0; padding: 0.55rem 0.8rem; list-style: none; display: flex; flex-direction: column; gap: 0.2rem; min-width: 9.5rem; background: color-mix(in srgb, rgb(var(--tile-rgb)) 82%, transparent); box-shadow: inset 0 0 0 1px var(--line); }
  .top3 li { display: flex; justify-content: space-between; gap: 1rem; font-size: 0.88rem; color: var(--fg-2); }
  .top3 b { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .top3 span { font-family: var(--font-num-sm, var(--font-num)); color: var(--fg); font-variant-numeric: tabular-nums; }
  .top3.is-below { left: 0; gap: 0; padding: 0.2rem 0.9rem; background: var(--field); box-shadow: none; }
  .top3.is-below li { height: 1.9rem; align-items: center; border-top: 1px solid var(--line); font-size: 0.95rem; }
  .top3.is-below li:first-child { border-top: 0; }
  [data-size="s"] .top, [data-size="s"] .top3 { display: none; }
</style>
<script>
  // the land as dots: 132 by 58 cells across the map, one bit each, Antarctica left off
  var MASK = { w: 132, h: 58, top: 20, height: 420, b64: "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAf//wAAABAAAAAAAAAAAAAA///wHAABgAAAAAAAAAAABz4//gAAGB/AgAAAAAAAAAP38H/AAAM3/+cAAAAAAAH5/2+H/AB4A////4gAAAAGf///nH8AD///////4AAAAG////PHhwH///////8AAAAB///4eHAAP///////8AAAAB///w4CAAff/////+4AAAABgf/w+AACOf/////gwAAAAAAf/9/AAHO//////g4AAAAAAf///gAHf//////4QAAAAAAf///AAD///////8AAAAAAAf//5gAB///////8AAAAAAAf//8AAB//9////4AAAAAAA///gAAH/8d////5AAAAAAA///AAAHj/+////xAAAAAAA//+AAAHBX+///+xAAAAAAA//8AAAH8X/////ZgAAAAAA//8AAAH8A/////HAAAAAAAf/wAAAH+Y/////kAAAAAAAf/wAAAP///////gAAAAAAAfwwAAAf//+////gAAAAAAAPwQAAAf///P///gAAAAAAADgAAAA///v4///QAAAAAAADmYAAA////4f38AAAAAAAAD+GAAA///34Pj0AAAAAAAAA8AAAA///3wPD4YAAAAAAAAPAAAA////AGB8IAAAAAAAADEAAA///8AGA8EAAAAAAAABPwAAf///gCAIEAAAAAAAAA/4AAf///ABAgEAAAAAAAAAf+AAPP//AABwwAAAAAAAAAf/AAAH/+AABxgAAAAAAAAAf/AAAH/8AAAzwAAAAAAAAA//wAAH/8AAAb4gAAAAAAAA//8AAD/4AAAZ4eAAAAAAAA//+AAD/4AAAMAPAAAAAAAAf/+AAD/4AAAHAHgAAAAAAAf/+AAB/4AAAAAAgAAAAAAAP/8AAB/4AAAAB0AAAAAAAAP/8AAD/5AAAAH2AAAAAAAAD/8AAD/zAAAAP+AAAAAAAAD/8AAD/jAAAAf+AAAAAAAAB/8AAB/nAAAB//AAAAAAAAB/wAAB/mAAAD//AAAAAAAAB/gAAB/AAAAD//gAAAAAAAB/gAAB/AAAAD//AAAAAAAAB/gAAA+AAAAD//AAAAAAAAB/AAAA8AAAADz+AAAAAAAAB+AAAAAAAAACB8AAAAAAAAB+AAAAAAAAAAA4BgAAAAAAB4AAAAAAAAAAAABAAAAAAAB4AAAAAAAAAAAwGAAAAAAAB4AAAAAAAAAAAAYAAAAAAABwAAAAAAAAAAAAQA" };
  // where each country sits on the same map (the middle of its main land), as x,y out of 1000 by 500
  var AT = {};
  "AE646,210 AF673,178 AL551,154 AM615,157 AO548,327 AR331,400 AT535,134 AU861,369 AZ622,157 BA545,145 BD744,210 BE511,124 BF495,247 BG564,149 BI583,298 BJ506,256 BN819,272 BO323,341 BR354,322 BS290,208 BT742,199 BW564,358 BY567,116 BZ257,232 CA268,105 CD565,296 CF556,266 CG542,290 CH520,136 CI484,263 CL315,405 CM535,269 CN769,170 CO297,274 CR267,255 CU285,217 CY586,174 CZ537,127 DE525,123 DJ617,249 DK522,107 DO307,226 DZ507,196 EC282,292 EE559,100 EG580,202 EH467,209 ER606,237 ES491,157 ET609,259 FI557,83 FJ987,344 FK357,453 FR506,137 GA532,289 GB494,115 GE610,151 GH497,262 GL416,58 GM457,244 GN469,253 GQ529,282 GR558,160 GT252,237 GW458,248 GY336,272 HN262,239 HR541,142 HT302,226 HU548,135 ID817,288 IE481,117 IL593,186 IN715,213 IQ615,181 IR643,182 IS459,81 IT531,147 JM288,229 JO597,186 JP856,171 KE605,285 KG690,153 KH789,246 KP826,158 KR832,170 KW627,193 KZ665,132 LA784,228 LB594,178 LK723,262 LR474,266 LS575,383 LT556,110 LU515,127 LV558,106 LY548,200 MA477,191 MD570,135 ME549,149 MG627,349 MK555,153 ML490,232 MM762,219 MN755,136 MR472,222 MW594,329 MX223,210 MY818,276 MZ597,342 NA546,358 NC950,355 NE525,231 NG522,256 NI266,246 NL513,119 NO530,84 NP725,196 NZ929,429 OM653,221 PA278,260 PE294,317 PG900,308 PH834,236 PK684,190 PL546,120 PR318,228 PS593,184 PT479,159 PY342,362 QA638,205 RO562,139 RS552,144 RU720,94 RW583,294 SA620,209 SB941,312 SD582,236 SE537,89 SI537,138 SK548,130 SL467,260 SN460,241 SO627,272 SR345,274 SS584,264 SV255,243 SY601,174 SZ584,372 TD551,238 TF670,446 TG503,260 TH777,239 TJ683,163 TL849,315 TM652,161 TN525,177 TR591,161 TT330,253 TW827,211 TZ596,307 UA576,129 UG590,283 US246,160 UY352,393 UZ661,152 VE317,264 VN791,234 VU959,336 XK553,150 YE630,236 ZA567,380 ZM576,330 ZW581,348".split(" ").forEach(function (s) { var p = s.slice(2).split(","); AT[s.slice(0, 2)] = [+p[0], +p[1]]; });
  var bits = Uint8Array.from(atob(MASK.b64), function (c) { return c.charCodeAt(0); });
  var cv = document.getElementById("cv"), ctx = cv.getContext("2d"), last = null;
  // a latitude and longitude on the same map, in the Natural Earth projection the land is drawn in
  function project(lon, lat) {
    var l = (lon * Math.PI) / 180, p = (lat * Math.PI) / 180, p2 = p * p, p4 = p2 * p2;
    var x = l * (0.8707 - 0.131979 * p2 + p4 * (-0.013791 + p4 * (0.003971 * p2 - 0.001529 * p4)));
    var y = p * (1.007226 + p2 * (0.015085 + p4 * (-0.044475 + 0.028874 * p2 - 0.005916 * p4)));
    return [500 + 182.79 * x, 287.07 - 182.79 * y];
  }
  function draw(d) {
    // one place per country, or per latitude and longitude, adding up the rows for each
    var by = {}, list = [];
    (d.items || []).forEach(function (it) {
      var v = typeof it.value === "number" ? it.value : 1, cc = String(it.country || "").toUpperCase(), at = null, key = it.label;
      if (typeof it.lat === "number" && typeof it.lon === "number") { at = project(it.lon, it.lat); key = it.lat + "," + it.lon; }
      else if (AT[cc]) { at = AT[cc]; key = cc; }
      if (!by[key]) list.push((by[key] = { label: it.label || cc || "Somewhere", v: 0, at: at }));
      by[key].v += v;
    });
    var ok = list.length > 0;
    document.getElementById("main").hidden = !ok;
    document.getElementById("empty").hidden = ok;
    if (!ok) return;
    list.sort(function (a, b) { return b.v - a.v; });
    var total = list.reduce(function (a, x) { return a + x.v; }, 0);
    board.count("#total", total);
    // what it means: how spread out it is, and where most of it comes from
    document.getElementById("what").textContent = "from " + (list.length === 1 ? "one place" : list.length + " places") + (list.length > 1 && total ? ", " + Math.round((list[0].v / total) * 100) + "% from " + list[0].label : "");
    var r = document.getElementById("map").getBoundingClientRect(), W = Math.max(1, r.width), H = Math.max(1, r.height);
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // the map keeps its shape, as big as its box allows; a tall box puts it at the top and ranks the places under it
    var rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 13, row = rem * 1.9;
    var aspect = 1000 / MASK.height, mw = W, mh = W / aspect;
    if (mh > H) { mh = H; mw = H * aspect; }
    var rows = Math.min(list.length, 8, Math.floor((H - mh - rem) / row)), below = rows >= 4;
    var ox = (W - mw) / 2, oy = below ? Math.max(0, (H - rows * row - rem * 0.8 - mh) / 2) : (H - mh) / 2, cw = mw / MASK.w, ch = mh / MASK.h, dot = Math.max(0.7, Math.min(cw, ch) * 0.3);
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = board.theme.fg3 || "gray";
    ctx.globalAlpha = 0.5;
    for (var j = 0; j < MASK.h; j++)
      for (var i = 0; i < MASK.w; i++) {
        var b = j * MASK.w + i;
        if ((bits[b >> 3] >> (7 - (b & 7))) & 1) { ctx.beginPath(); ctx.arc(ox + (i + 0.5) * cw, oy + (j + 0.5) * ch, dot, 0, 7); ctx.fill(); }
      }
    ctx.globalAlpha = 1;
    // a glowing dot where people are, bigger where there are more, with a ring that pings out of it
    var pins = "";
    list.filter(function (x) { return x.at; }).slice(0, 60).forEach(function (x, k) {
      var px = ox + (x.at[0] / 1000) * mw, py = oy + ((x.at[1] - MASK.top) / MASK.height) * mh;
      var size = Math.max(5, Math.min(cw * 3.4, 4 + Math.sqrt(x.v / list[0].v) * cw * 2.6));
      pins += '<i class="pin" title="' + board.esc(x.label + ": " + board.fmt(x.v)) + '" style="left:' + px.toFixed(1) + "px;top:" + py.toFixed(1) + "px;--d:" + size.toFixed(1) + "px;--delay:-" + ((k * 0.37) % 2.4).toFixed(2) + 's"></i>';
    });
    document.getElementById("pins").innerHTML = pins;
    // the top three in a corner, or the top places in a list under a map with room below it
    var top3 = document.getElementById("top3");
    top3.classList.toggle("is-below", below);
    top3.innerHTML = list.slice(0, below ? rows : 3).map(function (x) { return "<li><b>" + board.esc(x.label) + "</b><span>" + board.fmt(x.v) + "</span></li>"; }).join("");
    top3.hidden = !below && (W < 300 || H < 150);
  }
  board.onData(function (d) { last = d; draw(d); });
  board.onResize(function () { if (last) draw(last); });
  board.onTheme(function () { if (last) draw(last); });
</script>`;

export const WIDGET_EXAMPLES: WidgetExample[] = [
  { id: "map", name: "Signups map", blurb: "Where people came from, as glowing dots on a dotted world, with the top three places.", reads: "items", data: 'a stream of items, one per country or place: {items: [{label: "United States", country: "US", value: 41}, ...]}, with country a two-letter code, or lat and lon for a city', ask: "Where this week's signups came from, on a map like the example", size: [520, 312], html: MAP },
  { id: "uptime", name: "Uptime", blurb: "Each service's last weeks as a strip of days, green when it was up and amber or red when it was not, with its uptime and its worst day.", reads: "json", data: 'a stream whose json is {services: [{name: "api", now: "up", days: [100, 99.98, ...]}]}: each day the percent it was up, oldest first and ending today, and now "up", "slow" or "down" when the monitor says, as uptime monitors report them', ask: "Uptime for my three servers, like the example", size: [400, 248], html: UPTIME },
  { id: "compare", name: "This month vs last", blurb: "This month so far against the same days last month, as running totals, and plain about history that is too short.", reads: "points", data: "a stream with a point a day, like signups: {points: [{t, v}, ...]}", ask: "Compare this month's signups with last month's, like the example", size: [400, 300], html: COMPARE },
  { id: "goal", name: "Road to a goal", blurb: "How far a number is toward a target, and when it gets there at the rate of the last four weeks.", reads: "points", data: "a stream with a number and its history, like MRR: {value, points: [{t, v}, ...], format}", ask: "My MRR toward a goal, with when we get there, like the example", size: [400, 300], html: GOAL },
  { id: "posts", name: "Latest posts", blurb: "Your latest posts with their views and likes, the best one picked out, each opening the post.", reads: "items", data: 'a stream of items, newest first, one per post: {items: [{label: its text, value: its views, sub: "318 likes", t: when it went out, as epoch ms, href: its link}]}', ask: "My latest posts on X and how they are doing, like the example", size: [440, 280], html: POSTS },
  { id: "scores", name: "Site speed", blurb: "Lighthouse's scores as rings, colored the way Lighthouse colors them, with the one to work on.", reads: "items", data: 'a stream of items, one per category, scored 0 to 100: {items: [{label: "Performance", value: 98}, {label: "Accessibility", value: 100}, ...]}, as PageSpeed Insights reports them (its 0 to 1 scores are read as percents too)', ask: "Lighthouse scores for my landing page, like the example", size: [360, 280], html: SCORES },
  { id: "funnel", name: "Signup funnel", blurb: "Each step of a funnel, how many continue, and the rate end to end.", reads: "items", data: 'a stream of items, one per step: {items: [{label: "Visited", value: 12480}, {label: "Signed up", value: 1874}, ...]}', ask: "A signup funnel like the example", size: [400, 304], html: FUNNEL },
  { id: "mix", name: "Revenue mix", blurb: "Parts of a whole as a donut, with a legend that fits the space.", reads: "items", data: 'a stream of items, one per part: {items: [{label: "Pro", value: 8200}, ...], format}', ask: "A donut of my revenue by plan, like the example", size: [400, 264], html: MIX },
  { id: "heatmap", name: "Activity calendar", blurb: "Your recent days as a calendar, darker where more happened, with the year's total and the current streak.", reads: "points", data: "a stream with a point per event or per day: {points: [{t, v}, ...]}", ask: "A calendar heat map like the example", size: [520, 232], html: HEAT },
  { id: "weather", name: "Weather scene", blurb: "Today's weather as a small moving picture, from the weather source.", reads: "json", data: "the weather source's stream: value is the temperature, and json is {unit, current: {temp, feels, code, day}, daily: [{d, code, hi, lo, rain}, ...]}, with code an Open-Meteo weather code", ask: "Today's weather in my city as a small scene, like the example", size: [400, 300], html: WEATHER },
  { id: "calendar", name: "Month calendar", blurb: "The month at a glance, today ringed, a dot for each event, and the day's agenda beside it.", reads: "items", data: 'a calendar stream: items, one per event, {label, t: its start as epoch ms, sub}, with value "all-day" for an all-day event and t its date at midnight UTC, as an iCal source sends them', ask: "My calendar as a month view, like the example", size: [560, 360], html: CALENDAR },
  { id: "snake", name: "Snake", blurb: "The classic game, sized to its tile, in the board's colors, with your best score kept.", reads: null, ask: "", size: [420, 340], html: SNAKE },
  { id: "year", name: "Year in dots", blurb: "Every day of the year as a dot, with how much of it is gone.", reads: null, ask: "", size: [360, 280], html: YEAR },
  { id: "focus", name: "Focus timer", blurb: "Twenty-five minutes on, five off, and a count of today's sessions.", reads: null, ask: "", size: [300, 300], html: FOCUS },
  { id: "moon", name: "Moon tonight", blurb: "The moon's phase as it is tonight, and when it is next full or new.", reads: null, ask: "", size: [360, 200], html: MOON },
];

export const WIDGET_EXAMPLE: Record<string, WidgetExample> = Object.fromEntries(WIDGET_EXAMPLES.map((e) => [e.id, e]));
